import { useState, useEffect, useContext } from 'react';
import { Eye, Clock, Download, ChevronUp, ChevronDown, Smartphone, Search, Monitor } from 'react-feather'
import { format, subYears } from 'date-fns';
import { useStorage, useRemote } from '../../../Utils/Utils';
import { BannerContext } from '../../../InternalApiApp';
import { minutesIntoReadableFormat } from '../../../Utils/Utils';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import PageContentToggle from '../../../components/Menus/PageContentToggle'
import SimpleSearchFilter, { filterBy } from '../../../components/Input/SimpleSearchFilter'
import Table from '../../../components/Table/Table';
import FlexWrapper from '../../FlexWrapper';
import PageGridDivider from '../../Shared/Page/PageGridDivider';
import Divider from '../../../components/Divider';
import TickerSectorItem from '../../../components/TickerSectorItem';
import AnalyticsMetricFilter from '../../../components/Analytics/AnalyticsMetricFilter';
import LoadingIcon from '../../Shared/LoadingIcon';
import UsageMetrics from '../Components/UsageMetrics';
import TopAssetClass from '../Components/TopAssetClass';
import AnalyticsSourceSelector from "../AnalyticsSourceSelector";


const AnalyticsPortfolioPage = ({ datatita, jsonFile }) => {
    const { setBannerBreadCrumbs, setBannerAdditionalContent, setBacNoDivider } = useContext(BannerContext);

    //const { width } = useWindowDimensions();
    const storage = useStorage();
    const remote = useRemote();
    const tickerId = datatita.match.params.tickerId

    const usageMetrics = [
        {
            label: "Total Page Views",
            column: "count",
            index: 0,
            method: "reduce",
            valueStyle: { fontSize: "1.563rem" }
        },
        {
            label: "Average Duration",
            column: "minutes",
            index: 1,
            method: "reduce",
            divisor: {
                type: "metrics",
                keys: "Total Page Views"
            },
            valueStyle: { fontSize: "1.563rem" }
        }
    ]

    // states
    const [loading, setLoading] = useState(true);
    const [currentView, setCurrentView] = useState("Downloads");
    const [downloads, setDownloads] = useState([])
    const [portfolio, setPortfolio] = useState(null);
    const [sortingColumn, setSortingColumn] = useState({ column: "initiated", ascending: false })
    const [searchTerm, setSearchTerm] = useState("")
    const [managerLogoSrc, setManagerLogoSrc] = useState(null)
    const [views, setViews] = useState(null);
    const [uniqueViews, setUniqueViews] = useState(null);
    const [avgDuration, setAvgDuration] = useState(null);
    const [assetClassesUrls, setAssetClassesUrls] = useState([]);
    const [assetClassesObj, setAssetClassesObj] = useState([]);


    useEffect(() => {
        storage.getOrFetch(`assets/classes/list`).then((fetchedAssetClasses) => {
            setAssetClassesUrls(fetchedAssetClasses.map(c => `/portfolio/${tickerId}/allocation/${c.name.replace(/\s/g, "-").toLowerCase()}`))
            setAssetClassesObj(fetchedAssetClasses.reduce((a, c) => {
                return {
                    ...a, [`/portfolio/${tickerId}/allocation/${c.name.replace(/\s/g, "-").toLowerCase()}`]: {
                        name: c.name,
                        path: `/portfolio/${tickerId}/allocation/${c.name.replace(/\s/g, "-").toLowerCase()}`
                    }
                }
            }, {}))

            remote.get("analytics/aggregates/funds").then((fetchedFunds) => {
                const linkedFundStats = fetchedFunds.find((fund) => fund.number === tickerId)
                setUniqueViews(linkedFundStats?.views)
            })

            remote.post(`data/${tickerId}/get`, ["pm_logo"]).then(fetchedFunfData => {
                const src = fetchedFunfData && fetchedFunfData.data && fetchedFunfData.data[0] ? fetchedFunfData.data[0].replace("url:", "") : null
                setManagerLogoSrc(src)

                storage.getOrFetch(`/funds/funds/${tickerId}`).then((fetchedFund) => {
                    setBannerBreadCrumbs({ tab: 'Portfolios', param1: { name: fetchedFund.fundName, id: tickerId } })
                    setBacNoDivider(true)
                    setBannerAdditionalContent(<AnalyticsSourceSelector />)

                    remote.get(`/analytics/aggregates/webpage?from=${format(subYears(new Date, 6), 'yyyy-MM-dd')}&to=${format(new Date, 'yyyy-MM-dd')}`).then(fetchedWebPages => {
                        const relevantWebPages = fetchedWebPages.filter((webPage) => webPage.path && webPage.path.includes(tickerId))
                        const totalViews = relevantWebPages && relevantWebPages.length > 0 ? relevantWebPages.reduce((acc, webPage) => acc + (webPage.count || 0), 0) : null
                        const totalDuration = relevantWebPages && relevantWebPages.length > 0 ? relevantWebPages.reduce((acc, webPage) => acc + (webPage.minutes || 0), 0) : null

                        setViews(totalViews ? totalViews : "-")
                        setAvgDuration(totalDuration || totalDuration === 0 ? totalDuration > 0 ? minutesIntoReadableFormat(Math.ceil(totalDuration / totalViews)) : 0 + "m" : "-")

                        remote.get(`/analytics/fund/${tickerId}/downloads`).then((fetchedNotes) => {
                            setPortfolio(fetchedFund)
                            setDownloads(fetchedNotes)
                            setLoading(false)

                        }).catch(() => {
                            setPortfolio(fetchedFund)
                            setDownloads([])
                            setLoading(false)
                        })
                    })
                })
            })
        })
        return () => {
            setBacNoDivider(false)
            setBannerAdditionalContent(null)
        }
    }, [])

    function handleSort(a, b) {
        const { column, ascending } = sortingColumn;
        if (a[column] && typeof a[column] === 'number' && b[column] && typeof b[column] === 'number') {
            return ascending ? a[column] - b[column] : b[column] - a[column];
        } else if (a[column] === 0 || b[column] === 0) {
            return ascending ? (a[column] || 0) - (b[column] || 0) : (b[column] || 0) - (a[column] || 0);
        } else if (a[column] && typeof a[column] === 'string' && b[column] && typeof b[column] === 'string') {
            return ascending ? a[column].localeCompare(b[column]) : b[column].localeCompare(a[column]);
        }
    }

    function filterData(dataToFilter) {
        if (searchTerm === null || searchTerm.trim().size === 0) {
            return dataToFilter
        }
        return dataToFilter.filter((datum) => filterBy(datum, ["device", "browser", "locationCity", "locationCountry", "downloadType"], searchTerm))
    }

    function changeTab(string) {
        if (string === "Downloads") {
            setCurrentView("Downloads")
            setSortingColumn({ column: "Time/Date", ascending: false })
        }
        if (string === "Overview") {
            setCurrentView("Overview")
        }
        if (string === "Performance") {
            setCurrentView("Performance")
        }
        if (string === "Reports") {
            setCurrentView("Reports")
        }
        if (string === "Asset Allocation") {
            setCurrentView("Asset Allocation")
        }
    }

    // Portfolio metrics
    const metricsDownloads = downloads ? downloads.length : "-"

    // downloads table data 
    const downloadsColWidths = ["280px", "180px", "280px", "180px", "100px"]
    const downloadsHeaders = [
        {
            text: (
                <div className='sorting-header' onClick={() => setSortingColumn({ ascending: !sortingColumn.ascending, column: "initiated" })}>
                    <div className={sortingColumn.column === "initiated" ? "font-weight-semi-bold" : ""}>Time/Date</div>
                    {sortingColumn && sortingColumn.column === "initiated" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "initiated" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => setSortingColumn({ ascending: !sortingColumn.ascending, column: "downloadType" })} >
                    <div className={sortingColumn.column === "downloadType" ? "font-weight-semi-bold" : ""}>Download Type</div>
                    {sortingColumn && sortingColumn.column === "downloadType" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "downloadType" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => setSortingColumn({ ascending: !sortingColumn.ascending, column: "locationCity" })} >
                    <div className={sortingColumn.column === "locationCity" ? "font-weight-semi-bold" : ""}>User Location</div>
                    {sortingColumn && sortingColumn.column === "locationCity" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "locationCity" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => setSortingColumn({ ascending: !sortingColumn.ascending, column: "browser" })} >
                    <div className={sortingColumn.column === "browser" ? "font-weight-semi-bold" : ""}>Browser</div>
                    {sortingColumn && sortingColumn.column === "browser" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "browser" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header centered' onClick={() => setSortingColumn({ ascending: !sortingColumn.ascending, column: "device" })} >
                    <div className={sortingColumn.column === "device" ? "font-weight-semi-bold" : ""}>Device</div>
                    {sortingColumn && sortingColumn.column === "device" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "device" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        }
    ];

    const downloadsDataMap = (data) => data && data.length > 0 && data.map((datum, idx) => {
        const { device, browser, downloadType, locationCity, locationCountry, initiated } = datum       // change once the endpoint is implemented
        const location = (locationCity == null && locationCountry == null) ? "Unknown" : locationCity + ", " + locationCountry

        return (
            <tr key={idx}>
                <td>{initiated ? format(new Date(initiated), "hh:mmaaa dd/MM/yyyy") : "-"}</td>
                <td>{downloadType || "-"}</td>
                <td>{location || "-"}</td>
                <td>{browser || "-"}</td>
                <td className="text-align-center">
                    {device === "mobile"
                        ? <Smartphone size={24} style={{ transform: "translateY(-1px)" }} color="#0A70C9" />
                        : device === "desktop"
                            ? <Monitor size={24} style={{ transform: "translateY(-1px)" }} color="#0E8EFD" />
                            : "-"
                    }</td>
            </tr>
        );
    });

    const filteredData = filterData(downloads)

    if (!portfolio) {
        return (
            <Page noBanner showScrollbar>
                <PageContentBodyGrid showScrollbar>
                    <PageGridItem colSpan="12">
                        <LoadingIcon centered />
                    </PageGridItem>
                </PageContentBodyGrid>
            </Page>
        )
    }

    return (
        <Page noBanner noOverflownY>
            {/* model */}
            <PageContentBodyGrid showScrollbar style={{ height: "calc(100% - 7rem)" }}>
                <PageGridItem colSpan="12">
                    <FlexWrapper gap="xl">
                        <FlexWrapper gap="xs" direction="column" align="flex-start">
                            {/*TODO: update image url*/}
                            <TickerSectorItem imageClass='manager-logo-image-container' label={portfolio.fundNumber} country={portfolio.country} filename={portfolio.ric} image={managerLogoSrc} />
                        </FlexWrapper>
                        <Divider vertical height={40} />
                        <AnalyticsMetricFilter
                            label="Unique Views"
                            icon={<Eye />}
                            count={uniqueViews}
                            pointer
                        />
                        <AnalyticsMetricFilter
                            label="Total Views"
                            icon={<Eye />}
                            count={views}
                            pointer
                        />
                        <AnalyticsMetricFilter
                            label="Avg Duration"
                            icon={<Clock />}
                            count={avgDuration}
                            pointer
                        />
                        <AnalyticsMetricFilter
                            label="Downloads"
                            icon={<Download color='#00805e' />}
                            count={metricsDownloads}
                            pointer
                        />
                    </FlexWrapper>
                </PageGridItem>
                <PageGridDivider colSpan="12" />
                <PageGridItem colSpan="12">
                    <FlexWrapper gap="m">
                        <PageContentToggle labels={[
                            { label: "Downloads", active: currentView === "Downloads", onClick: () => changeTab("Downloads") /*setCurrentView("research") */ },
                            { label: "Overview", active: currentView === "Overview", onClick: () => changeTab("Overview") /*setCurrentView("watching")*/ },
                            { label: "Performance", active: currentView === "Performance", onClick: () => changeTab("Performance") /*setCurrentView("watching")*/ },
                            { label: "Asset Allocation", active: currentView === "Asset Allocation", onClick: () => changeTab("Asset Allocation") /*setCurrentView("watching")*/ },
                            { label: "Reports", active: currentView === "Reports", onClick: () => changeTab("Reports") /*setCurrentView("watching")*/ }
                        ]} />
                        <Divider vertical height={24} />
                        <SimpleSearchFilter
                            size="small"
                            width="l"
                            placeholder={`Search`}
                            isClearable
                            disabled={currentView !== "Downloads"}
                            icon={<Search />}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                        />

                    </FlexWrapper>
                </PageGridItem>
                <PageGridDivider colSpan="12" />
                {currentView === 'Downloads' &&
                    <PageGridItem colSpan="12" style={{ paddingTop: "0.5rem" }}>
                        <Table
                            minWidth={1000}
                            tableStyle={{ maxWidth: 1200 }}
                            colWidths={downloadsColWidths}
                            headers={downloadsHeaders}
                            dataMap={downloadsDataMap(filteredData.sort(handleSort))}
                            dataLoading={loading}
                            suggestionMessage
                        />
                    </PageGridItem>
                }
                {currentView !== 'Downloads' &&
                    <>
                        <PageGridItem container col="1 / span 12" style={{ maxWidth: "1200px" }} /*col="1 / span 7*/>
                            <PageContentBodyGrid rowGap='none' paddingTop="0px" gridTemplateColumns='1fr'>
                                <UsageMetrics
                                    timeFrame={jsonFile?.mainTab?.dateRanges[0]}
                                    withHeader={true}
                                    dateRanges={jsonFile?.mainTab?.dateRanges}
                                    passedMetrics={usageMetrics}
                                    webpage
                                    url={`/portfolio/${tickerId}${currentView === 'Overview' ? "" : currentView === 'Performance' ? "/performance" : currentView === 'Reports' ? '/reports' : '/allocation'}`}
                                />
                                {currentView === "Asset Allocation" &&
                                    <FlexWrapper gap="m" style={{ paddingTop: "1.5rem" }}>
                                        <div style={{ width: "100%" }}>
                                            <TopAssetClass suggestionMessage rowSize="s" dateRanges={jsonFile?.mainTab?.dateRanges} type="views" ticker={tickerId} />
                                        </div>
                                        {/* <Divider vertical height={"auto"} />
                                        <div>
                                            <TopAssetClass suggestionMessage rowSize="s" dateRanges={jsonFile?.mainTab?.dateRanges} type="duration" ticker={tickerId} />
                                        </div> */}
                                    </FlexWrapper>
                                }
                            </PageContentBodyGrid>
                        </PageGridItem>
                        {/* <PageGridItem container col="8 / span 5" style={{ paddingBottom: 0 }}>
                            <PageContentBodyGrid rowGap='none' paddingTop={width < 1200 ? "1.5rem" : "0px"} gridTemplateColumns='1fr' divider={width < 1200 ? "top" : "left"}>
                                <BrowserChart
                                    options={[{ label: "Device Usage", value: "Device" }, { label: "Browser Usage", value: "Browser" }]}
                                    dateRanges={jsonFile?.mainTab?.dateRanges}
                                    minMenuWidth={134}
                                />
                            </PageContentBodyGrid>
                        </PageGridItem> */}
                    </>
                }
            </PageContentBodyGrid>
        </Page>
    );
}

export default AnalyticsPortfolioPage;